import React from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/SiteLayout";
import HeroBadge from "@/components/HeroBadge";
import useScrollReveal from "@/hooks/useScrollReveal";
import { ClipboardList, UserPlus, CreditCard, IdCard, HeartHandshake, RefreshCw, ArrowRight } from "lucide-react";

const STEPS = [
  { icon: ClipboardList, t: "Choose your plan", d: "Pick Individual (₹2,000), Husband Wife (₹3,000) or Family (₹6,000) — all annual, all-inclusive prices." },
  { icon: UserPlus, t: "Register", d: "Fill applicant details, family members (if applicable) and upload basic documents." },
  { icon: CreditCard, t: "Pay online", d: "Secure Razorpay checkout. Apply your discount code to unlock extras." },
  { icon: IdCard, t: "Get your card", d: "A premium digital NPC card with QR is issued instantly. Download or share." },
  { icon: HeartHandshake, t: "Use your benefits", d: "Show your NPC card at Nirmal Hospital, Adajan to unlock OPD, lab and pharmacy savings." },
  { icon: RefreshCw, t: "Renew later", d: "Get reminders 30/15/7 days before expiry. One-tap renewal in your dashboard." },
];

export default function HowItWorks() {
  useScrollReveal();
  return (
    <SiteLayout>
      <section className="hero-grad relative overflow-hidden">
        <div className="absolute inset-0 dot-grid opacity-50 pointer-events-none" />
        <div className="mx-auto max-w-7xl px-6 lg:px-10 pt-16 pb-12 relative">
          <div className="max-w-3xl reveal">
            <HeroBadge>How It Works · <span className="text-[#A53A2A]">6 Simple Steps</span></HeroBadge>
            <h1 className="mt-5 font-display text-5xl sm:text-6xl lg:text-7xl font-bold text-[#0F2540] leading-[1.04]">
              From sign-up to <span className="bg-gradient-to-r from-[#A53A2A] via-[#C89B3C] to-[#A53A2A] bg-clip-text text-transparent">savings</span> — in 5 minutes.
            </h1>
            <p className="mt-6 max-w-2xl text-lg text-[#5C6B7A]">
              The Nirmal Privilege Card was designed to be effortless. Here is the entire member journey, from first visit to renewal.
            </p>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 lg:px-10 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {STEPS.map((s, i) => (
            <div key={s.t} className="reveal lux-card rounded-2xl p-7 relative" style={{ animationDelay: `${i * 80}ms` }}>
              <div className="absolute top-7 right-7 font-display text-5xl font-bold text-[#1E4F87]/10">{String(i + 1).padStart(2, "0")}</div>
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-[#1E4F87] to-[#0F2540] text-white grid place-items-center shadow-lg">
                <s.icon className="h-6 w-6" />
              </div>
              <h3 className="mt-5 font-display text-xl font-bold text-[#0F2540]">{s.t}</h3>
              <p className="mt-2 text-sm text-[#5C6B7A] leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 lg:px-10 py-12">
        <div className="rounded-[2rem] mesh-dark p-10 lg:p-14 relative overflow-hidden reveal">
          <div className="absolute inset-0 dot-grid opacity-15 pointer-events-none" />
          <div className="relative grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <span className="overline text-[#C89B3C]">Ready when you are</span>
              <h3 className="mt-3 font-display text-3xl lg:text-4xl font-bold text-white">Activate your NPC in 5 minutes.</h3>
              <p className="mt-3 text-white/80 max-w-xl">Online, instant, refundable within 14 days.</p>
            </div>
            <div className="lg:col-span-4 flex lg:justify-end">
              <Link to="/join" className="bg-white text-[#0F2540] px-7 py-4 rounded-full font-semibold inline-flex items-center gap-2 hover:bg-[#C89B3C] transition shadow-lg">Join Now <ArrowRight className="h-4 w-4" /></Link>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
